import { useEffect, useState } from "react";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "../../utils/toast";
import { resolveAxiosErrorMessage } from "../../utils/apiValidationErrors";

const StarIcon = ({ filled = false, size = 26 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill={filled ? "currentColor" : "#e6e6e6"}
  >
    <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const validationSchema = Yup.object({
  name: Yup.string()
    .min(2, "Minimum 2 characters required")
    .max(100, "Maximum 100 characters")
    .matches(/^[a-zA-Z\s.]+$/, "Name may only contain letters")
    .required("Name is required"),
  email: Yup.string()
    .transform((value) => (value === "" || value == null ? undefined : value))
    .email("Invalid email address"),
  phone: Yup.string()
    .matches(/^\d{10}$/, "Phone must be exactly 10 digits")
    .required("Phone is required"),
  city: Yup.string()
    .min(2, "Minimum 2 characters required")
    .max(100, "Maximum 100 characters")
    .matches(/^[a-zA-Z\s.-]+$/, "City may only contain letters")
    .required("City is required"),
  rating: Yup.number()
    .min(1, "Select rating")
    .max(5, "Select rating")
    .required("Rating is required"),
  review: Yup.string()
    .min(10, "Minimum 10 characters required")
    .max(500, "Maximum 500 characters")
    .required("Review is required"),
});

const emptyFormValues = {
  name: "",
  email: "",
  phone: "",
  city: "",
  rating: 0,
  review: "",
};

const readStoredCustomer = () => {
  try {
    const raw = localStorage.getItem("user_data") || localStorage.getItem("user");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const profileToFormValues = (customer = {}) => {
  const fullName = (
    customer.name ||
    `${customer.first_name || ""} ${customer.last_name || ""}`
  ).trim();
  const phone = String(customer.phone || "").replace(/\D/g, "").slice(-10);

  return {
    ...emptyFormValues,
    name: fullName,
    email: customer.email || "",
    phone,
    city: localStorage.getItem("selectedCity") || "",
  };
};

const WriteReviewForm = ({ onSuccess }) => {
  const [formDefaults, setFormDefaults] = useState(emptyFormValues);

  useEffect(() => {
    const loadFormDefaults = async () => {
      const stored = profileToFormValues(readStoredCustomer());
      setFormDefaults(stored);

      const token = localStorage.getItem("user_token");
      if (!token) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_APP_DOMAIN}/api/customer/profile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.status && response.data.customer) {
          setFormDefaults(profileToFormValues(response.data.customer));
        }
      } catch {
        // Keep localStorage values if profile fetch fails.
      }
    };

    loadFormDefaults();
  }, []);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/reviews`,
        { ...values, channel: "website" }
      );
      if (response.data?.status === true) {
        toast.success(
          Number(values.rating) <= 3
            ? "Thanks for the feedback. We’ll make the next ride better."
            : "Thank you for your review! We appreciate your feedback."
        );
        resetForm({ values: formDefaults });
        if (onSuccess) onSuccess(values);
      } else {
        toast.error(response.data?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(resolveAxiosErrorMessage(error) || "Server error. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={formDefaults}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <Form className="write_review_form">
          <div className="write_review_row">
            <div>
              <label className="form-label">Name *</label>
              <Field type="text" name="name" className="form-control" placeholder="Full name" />
              <ErrorMessage name="name" component="div" className="text-danger small" />
            </div>
            <div>
              <label className="form-label">City *</label>
              <Field type="text" name="city" className="form-control" placeholder="Your city" />
              <ErrorMessage name="city" component="div" className="text-danger small" />
            </div>
          </div>
          <div className="write_review_row">
            <div>
              <label className="form-label">Phone *</label>
              <Field
                type="text"
                name="phone"
                className="form-control"
                placeholder="10 digit phone"
                maxLength="10"
                inputMode="numeric"
              />
              <ErrorMessage name="phone" component="div" className="text-danger small" />
            </div>
            <div>
              <label className="form-label">Email</label>
              <Field
                type="email"
                name="email"
                className="form-control"
                placeholder="Email address"
              />
              <ErrorMessage name="email" component="div" className="text-danger small" />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label">Rating *</label>
            <div className="write_review_stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  type="button"
                  key={star}
                  className={star <= values.rating ? "active" : ""}
                  onClick={() => setFieldValue("rating", star)}
                  aria-label={`${star} star`}
                >
                  <StarIcon filled={star <= values.rating} size={26} />
                </button>
              ))}
              <span>{values.rating ? `${values.rating}.0` : "Select rating"}</span>
            </div>
            <ErrorMessage name="rating" component="div" className="text-danger small" />
          </div>
          <div className="mb-2">
            <label className="form-label">Review *</label>
            <Field
              as="textarea"
              name="review"
              className="form-control"
              rows="3"
              maxLength="500"
              placeholder="How was your ride?"
            />
            <div className="write_review_counter">{values.review.length}/500</div>
            <ErrorMessage name="review" component="div" className="text-danger small" />
          </div>
          <button type="submit" className="write_review_submit" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit Review"}
          </button>
        </Form>
      )}
    </Formik>
  );
};

export default WriteReviewForm;
